import { InteractionType } from '@azure/msal-browser';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal, useMsalAuthentication } from '@azure/msal-react';
import React, { useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ClientConfig } from '../../clientConfig';
import '../../style.css';
import { OPENID_PARAMS } from '../../types/openid_enum';
import { ClientConfigContext } from '../clientConfigContext';
import { useBrand } from '../hooks/useBrand';
import { getClientCorrelationId } from '../logger/logMetadata';
import { buildAuthenticationRequest } from '../util/buildAuthenticationRequest';
import { initializeSessionTimeout, isNotMobile, manageAuthOnStateChange } from '../util/sessionTimeout';
import './App.css';
import LegalLinks from './elements/LegalLinks';
import { UNAUTHENTICATED_PAGES } from './routers';
import AuthenticatedRouter from './routers/AuthenticatedRouter';
import UnauthenticatedRouter from './routers/UnauthenticatedRouter';

function App(): JSX.Element {
    const location = useLocation();
    const brandInfo = useBrand();

    const searchParams = new URLSearchParams(document.location.search);
    const correlationId = getClientCorrelationId();

    const clientConfiguration: ClientConfig = useContext(ClientConfigContext);
    const request = buildAuthenticationRequest({ brandInfo, correlationId, scopes: clientConfiguration.b2cTenantConfig.scopes, searchParams });

    // If we're not on an unauthenticated page, then authentication is required.
    const requireAuthentication = !UNAUTHENTICATED_PAGES.includes(location.pathname);

    // On React app startup, on authenticated pages, it performs a silent login to the IDP if there are no local session.
    // If a local session exists, then no calls to the IDP are made.
    const msalAuthentication = requireAuthentication ? useMsalAuthentication(InteractionType.Silent, request) : null;
    const result = msalAuthentication?.result ?? null;
    const { instance, accounts, inProgress } = useMsal();

    useEffect(() => {
        if (msalAuthentication?.error && requireAuthentication) {
            msalAuthentication.login(InteractionType.Redirect, request);
        }
    }, [msalAuthentication?.error]);

    useEffect(() => {
        manageAuthOnStateChange(instance, accounts, result, inProgress, clientConfiguration.userActivityLimitMs);
    }, [msalAuthentication?.result, accounts, inProgress]);

    /**
     * [SPI-3914] Prevent the logout timer functionality in mobile workflow. Mobile App can leave a hidden view of PIP
     * in the app and that would eventually cause an undesired hard logout.
     */
    if (requireAuthentication && isNotMobile(searchParams)) {
        initializeSessionTimeout(instance, clientConfiguration.userActivityLimitMs);
    }

    if (UNAUTHENTICATED_PAGES.includes(location.pathname)) {
        return (
            <>
                <AuthenticatedTemplate>
                    <UnauthenticatedRouter />
                    <LegalLinks />
                </AuthenticatedTemplate>
                <UnauthenticatedTemplate>
                    <UnauthenticatedRouter />
                    <LegalLinks />
                </UnauthenticatedTemplate>
            </>
        );
    } else {
        return (
            <AuthenticatedTemplate>
                <AuthenticatedRouter />
                <LegalLinks />
            </AuthenticatedTemplate>
        );
    }
}

export default App;
